import React, { ReactNode } from 'react';

import { useField } from '@react-aria/label';
import { mergeProps } from '@react-aria/utils';
import { SapphireStyleProps, useThemeCheck } from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { Field } from '../../Field';
import { Label } from '../../Label';

export type FieldsetProps = GlobalDomAttributes &
  SapphireStyleProps & {
    /**
     * A label that describes the groups of fields.
     * The label is required because a fieldset that is not labelled is not
     * better than separate labelled fields.
     */
    label?: ReactNode;
    /**
     * Whether the input is disabled.
     * */
    isDisabled?: boolean;
    /**
     * Whether the input is required.
     * */
    isRequired?: boolean;
    /**
     * Whether the input should render as having an error and an error message.
     * This also sets the appropriate `aria` attributes on the input.
     *
     * A `boolean` sets the error state. Can be paired with the "note" prop with Field.Note variant="error"
     * A `ReactNode` sets the error state with an additional error message.
     *
     * **An error message is strongly recommended because an error should
     * always have an explanation about how to fix it.**
     */
    error?: boolean | ReactNode;
    /**
     * A note to show below the input.
     * If the input has an error message, this note will be replaced by that.
     * Use {@Link Field.Note} to render the note.
     */
    note?: ReactNode;
    /**
     * A HelpButton to render next to the label.
     */
    contextualHelp?: ReactNode;
    /**
     * @default 'lg'
     */
    size?: 'lg' | 'md';
    /**
     * To visually indicate if this field is required or optional.
     * @default false
     */
    necessityIndicator?: boolean;
    /**
     * Children should be two or more form controls without labels, notes or
     * error messages.
     *
     * We recommend you only use these fields inside: `<TextField>`, `<Select> `,
     * `<SearchableSelect>`, `<MultiSelect>`, `<SearchableMultiSelect>`,
     * `<DateField>` or `<DateRangeField>`.
     *
     * Example:
     * ```jsx
     * <Fieldset label="Phone Number">
     *   <Select aria-label="Country code" placeholder="+00" width={90}>
     *     <SelectItem>+40</SelectItem>
     *     <SelectItem>+42</SelectItem>
     *     <SelectItem>+45</SelectItem>
     *   </Select>
     *   <TextField
     *     aria-label="Phone number"
     *     placeholder="000-000-0000"
     *   ></TextField>
     * </Fieldset>
     * ```
     */
    children: ReactNode;

    /**
     * Places the label either above (default) or on the side of the control.
     * @default 'above'
     */
    labelPlacement?: 'side' | 'above';
  };

/**
 * Sapphire search input field.
 */
export const Fieldset = ({
  label,
  contextualHelp,
  note,
  error,
  isDisabled,
  isRequired,
  necessityIndicator,
  children,
  size = 'lg',
  labelPlacement = 'above',
  ...props
}: FieldsetProps): React.JSX.Element => {
  useThemeCheck();

  const { fieldProps, descriptionProps, errorMessageProps } = useField({
    isInvalid: !!error,
    label,
  });

  return (
    <Field
      elementType="fieldset"
      isDisabled={isDisabled}
      size={size}
      labelPlacement={labelPlacement}
      noDefaultWidth
      {...mergeProps(props, fieldProps)}
    >
      <Field.Context
        descriptionProps={error ? errorMessageProps : descriptionProps}
      >
        {label && (
          <Field.Label elementType="legend">
            <Label
              size={size}
              necessityIndicator={
                isRequired && necessityIndicator
                  ? 'required'
                  : !isRequired && necessityIndicator
                  ? 'optional'
                  : undefined
              }
              contextualHelp={contextualHelp}
            >
              {label}
            </Label>
          </Field.Label>
        )}
        <Field.Control isGroup>{children}</Field.Control>
        {((!!error && typeof error !== 'boolean') || note) && (
          <Field.Footer>
            {!!error && typeof error !== 'boolean' ? (
              <Field.Note variant="error">{error}</Field.Note>
            ) : note ? (
              note
            ) : (
              <></>
            )}
          </Field.Footer>
        )}
      </Field.Context>
    </Field>
  );
};
